// Jam: playing along (issue #131). While Jam is on, the keys 1–8 (pads on a phone) each play one note over the track
// (site/js/app.js, player.jam). The eight are picked from the chord sounding when the note lands, low to high, so every
// one of them fits; the note lands on the track's next 8th note (a swung one where the style swings its 8ths), like a
// tapped spot's sound lands on the beat (taps.js); and it sounds in a soft voice of the track's style, into the music
// bus, so the record's tone, tape and wobble, the music slider and mute reach it like the band.
//   nextEighth(start, bpm, at, late) · eighthLate(plan) · jamScale(key, plan) · jamPool(tones, root, key, plan)
//   jamNotes(tones, root, key, plan)   (pure: tests/jam.test.mjs)
//   createJam(ac, mixer, { registry, lite }) → { note(when, midi, style, i), stats(), dispose() }
import { clamp } from '../engine/core.js';
import { mtof } from './dsp.js';
import { PENTA } from './harmony.js';
import { swing } from './rhythm.js';
import { nextBeat, ownScale } from './taps.js';
import { AND } from './styles/dub-voices.js';
import { pulseWave } from './styles/chiptune-voices.js';
import { pluck as nylon } from './styles/bossa-guitar.js';
import { pluck as steel } from './styles/guitar-voice.js';
import { course } from './styles/medieval-lute.js';
import { kotoString } from './styles/japanese-koto.js';
import { pluck as oud } from './styles/oud-voice.js';
import { sitarPluck } from './styles/sitar-voice.js';

export const JAM_KEYS = 8;
export const JAM_AHEAD = 0.02;        // s: a note is never scheduled nearer than this, so the audio thread has it in time
// The eight notes start at the first one at or over JAM_LO (above the chords, whose top is MIDI 76 at most, among the
// lead's upper notes; the plucked voices start lower, STRING_LO below); a chord with few notes to offer reaches under
// it instead of over JAM_HI.
export const JAM_LO = 72, JAM_HI = 96;
const mod12 = n => ((n % 12) + 12) % 12;

/* ---------------- when: the next 8th note ---------------- */
// The 8ths off the beat (the "and"s), where a style swings them: how many 16ths late (dub-voices.js AND, what dub's
// hats, skank and bass play). The engine's own swing moves the odd 16ths only, which no 8th is on.
const SWUNG = { dub: AND };
/** How late (s) the "and"s of a track sit: 0 in every style that plays its 8ths straight. */
export function eighthLate(plan) {
  const and = SWUNG[plan?.vibe?.style];
  if (!and) return 0;
  const sd = 15 / plan.bpm;
  return and * sd + swing(2 + and, sd, plan.swing);
}
/** The first 8th note at or after `at` on the grid of a track that started at `start` (ac seconds) at `bpm`: nextBeat's
 *  grid, twice as fine, so it runs on before the track's first bar and past its last. `late` (eighthLate, under an
 *  8th): the "and"s sit that much after their place on the grid. */
export function nextEighth(start, bpm, at, late = 0) {
  const on = nextBeat(start, bpm * 2, at);
  if (!late) return on;
  const e = 30 / bpm, and = k => Math.abs(k % 2) === 1, n = Math.round((on - start) / e);
  if (and(n - 1) && on - e + late >= at - 1e-9) return on - e + late;   // the "and" before it is still to come
  return and(n) ? on + late : on;
}

/* ---------------- what: the eight notes that fit a chord ---------------- */
/** The track's scale (semitones over its tonic): its style's own where it plays in one (a maqam, a Japanese scale, a
 *  raga, a church mode), else the key's pentatonic, which the lead sings in. */
export const jamScale = (key, plan = null) => ownScale(plan) ?? PENTA[key?.mode] ?? PENTA.minor;

// A scale note a semitone under a sounding tone fits only as one of these (semitones over the chord's root → what the
// tones T over that root must be): the 9th of a minor chord, the 11th under a ♭5, the ♯11 of a chord with a major third,
// the 13th under a ♭7, and the major 7th of a major chord without a ♭7.
const UNDER = {
  2: T => T.has(3), 5: T => T.has(6) && !T.has(7), 6: T => T.has(7) && T.has(4), 9: T => T.has(10), 11: T => T.has(4) && !T.has(10),
};
/**
 * The pitch classes (0–11, sorted) that fit a chord sounding the pitch classes `tones` over the root `root` (a pitch
 * class too; the engine's harmonyAt gives both, from the keys' own voicing) in `key` on a track planned as `plan`: the
 * tones themselves, and the notes of the track's scale (jamScale) they leave room for. A scale note a semitone over a
 * sounding tone never fits (the 4th on a major chord, the ♭6 over a 5th, the ♭2 over a root), and one a semitone
 * under a sounding tone only as a tension that chord takes (UNDER).
 */
export function jamPool(tones, root, key, plan = null) {
  const T = new Set(tones.map(pc => mod12(pc - root))), pool = new Set(tones.map(mod12));
  for (const i of jamScale(key, plan)) {
    const d = mod12(key.tonic + i - root);
    if (T.has(mod12(d - 1)) || (T.has(mod12(d + 1)) && !UNDER[d]?.(T))) continue;
    pool.add(mod12(key.tonic + i));
  }
  return [...pool].sort((a, b) => a - b);
}
/** The MIDI notes of the eight keys over that chord, low to high: the notes of jamPool from the first at or over `lo`
 *  (the voice's: jamLow), moved down a note at a time while the top one is over JAM_HI (a chord that offers three notes
 *  spans over two octaves). */
export function jamNotes(tones, root, key, plan = null, lo = JAM_LO) {
  const pool = jamPool(tones, root, key, plan), notes = [];
  for (let m = lo - 24; m <= JAM_HI + 24; m++) if (pool.includes(mod12(m))) notes.push(m);
  let k = notes.findIndex(m => m >= lo);
  while (k > 0 && notes[k + JAM_KEYS - 1] > JAM_HI) k--;
  return notes.slice(k, k + JAM_KEYS);
}

/* ---------------- how: a soft voice of the track's style ---------------- */
// Levels: `level` is the bus into the music bus (the track buses' 0.5), `send` its share to the reverb. Each voice's own
// gain was set from one note alone through the mixer at the default music slider, beside the band (a jazzhop track's
// music alone sits at −19.3 dBFS): the loudest 400 ms of a struck voice's note is 4 to 6 dB under that, as loud as one
// note of a comp chord and 4 dB under the soft lead's; a plucked one 6 to 8 dB under (its pick peaks 12 dB over its
// ring, at −12 to −17 dBFS), the sitar, nearly all pick up there, 10 to 12. Notes landing together share that (each
// 1/√n), so all eight keys at once are no louder than one (within 1.7 dB) and peak under −10 dBFS, 6 dB from the
// limiter.
export const JAM = { level: 0.5, send: 0.3 };
export const JAM_MAX = 8;             // notes sounding at once: one more lets the oldest go
// The string models are made for their instruments' own notes: they thin out up the neck, and from MIDI 88 up one after
// another stops decaying and overflows (the oud and the nylon guitar at 88, the lute, the koto and the sitar at 89, the
// steel guitar at 97). So the plucked voices start their eight notes a fourth lower (STRING_LO), where the instruments
// play their tunes, and a note over STRING_TOP (a thin chord's top keys) is the string an octave under it, played twice
// as fast: a shorter string, plucked the same way.
export const STRING_LO = 67, STRING_TOP = 84;
const strung = fn => (ac, m) => { const up = m > STRING_TOP ? 1 : 0, p = fn(ac, m - 12 * up); return { buf: p.buf, rate: p.rate * (1 + up) }; };
const buffer = fn => strung((ac, m) => ({ buf: fn(ac, m), rate: 1 }));
/** The voices. A struck one is oscillators: `fm` [ratio, index, the share it falls to, τ] on a sine (and a `tine`
 *  [ratio, index, τ]), or `partials` [ratio, level, share of the ring] of sines or of `wave`; `lp` a low-pass that closes
 *  [from × f, to × f, τ] or stays [, , , Hz]; up in `atk`, down to settle[0] (τ settle[1]), then ringing off (τ `ring`).
 *  A plucked one is its style's own string: `string(ac, midi)` → { buf, rate }, that module's cached Karplus–Strong
 *  buffer of the note (RMS .2 over its first .3 s) and the rate to play it at. `gain`: its level (above). */
export const JAM_VOICES = {
  // the jazzhop Rhodes (instruments.js createEP): a 1:1 FM tone bar, its index falling to a quarter, and the tine's
  // 14:1 tick where that stays inside the band
  rhodes: { gain: 0.43, fm: [1, 1.7, 0.25, 0.3], tine: [14, 0.3, 0.025], atk: 0.003, settle: [0.55, 0.09], ring: 0.42 },
  // a felt piano's first three partials (piano-instruments.js: the 2nd at −9 dB, the 3rd −13), a slower hammer, the
  // felt closing from 3.4 f to 1.8 f as the note decays
  felt: { gain: 0.32, partials: [[1, 1, 1], [2, 0.35, 0.6], [3, 0.22, 0.4]], lp: [3.4, 1.8, 0.2], atk: 0.007, settle: [0.42, 0.16], ring: 0.5 },
  // a struck glass bar (taps.js's glass): the fundamental and a free bar's inharmonic modes, a soft mallet, a long ring
  glass: { gain: 0.25, partials: [[1, 1, 1], [2.76, 0.3, 0.45], [5.4, 0.12, 0.25]], atk: 0.008, settle: [0.8, 0.2], ring: 0.6 },
  // a music box tooth (spooky-voices.js): a little octave in the tone, the bright strike at 2.76 f dying first
  box: { gain: 0.36, partials: [[1, 1, 1], [2, 0.14, 1], [2.76, 0.45, 0.3]], atk: 0.0025, settle: [0.6, 0.08], ring: 0.5 },
  // a 25 % pulse channel (chiptune-voices.js pulseWave): a blip falling to a third, under a 4 kHz low-pass
  chip: { gain: 0.6, wave: ac => pulseWave(ac, 0.25), partials: [[1, 1, 1]], lp: [0, 0, 0, 4000], atk: 0.003, settle: [0.35, 0.1], ring: 0.32 },
  nylon: { gain: 0.8, lo: STRING_LO, string: buffer((ac, m) => nylon(ac, m, 'finger')) },
  steel: { gain: 0.85, lo: STRING_LO, string: buffer(steel) },
  lute: { gain: 0.8, lo: STRING_LO, string: buffer(course) },
  koto: { gain: 0.75, lo: STRING_LO, string: buffer(kotoString) },
  oud: { gain: 0.8, lo: STRING_LO, string: buffer(oud) },
  sitar: { gain: 0.6, lo: STRING_LO, string: strung((ac, m) => { const p = sitarPluck(ac, m); return { buf: p.buf, rate: p.tune }; }) },
};
/** style id → its voice */
export const JAM_VOICE = {
  jazzhop: 'rhodes', neosoul: 'rhodes', house: 'rhodes', dnb: 'rhodes', dub: 'rhodes', synth: 'rhodes',
  piano: 'felt', sad: 'felt', classical: 'felt', ambient: 'glass', spooky: 'box', chiptune: 'chip',
  bossa: 'nylon', guitar: 'steel', medieval: 'lute', japanese: 'koto', oud: 'oud', sitar: 'sitar',
};
/** the id of a style's voice (one without its own plays the Rhodes) */
export const jamVoice = style => JAM_VOICE[style] ?? 'rhodes';
/** the note a style's eight keys start from (jamNotes' `lo`) */
export const jamLow = style => JAM_VOICES[jamVoice(style)].lo ?? JAM_LO;

/**
 * createJam(ac, mixer, { registry, lite }) → { note, stats, dispose }
 * note(when, midi, style, i): one note at ac time `when` in `style`'s voice, key `i` (0–7) a little left to right
 * (pan ∓.3); false when that note is already landing then (a key pressed twice inside one 8th). Every note is a
 * one-shot that rings off by itself (under 3.5 s), so nothing piles up: at most JAM_MAX sound (6 in lite), the oldest
 * let go (τ 12 ms) for one more, and a note struck again lets its last one go. Every node goes in the registry.
 * A second set of the same voices (the scene layers' twinkle, layers.js) names its own `tag`, bus `level` and `send`.
 */
export function createJam(ac, mixer, { registry, lite = false, tag = 'jam', level = JAM.level, send: wet = JAM.send }) {
  const bus = ac.createGain(), send = ac.createGain(), cap = lite ? 6 : JAM_MAX;
  bus.gain.value = level; send.gain.value = wet;
  bus.connect(mixer.musicIn); bus.connect(send); send.connect(mixer.revIn);
  const busVoice = registry.add({ src: [], all: [bus, send], tag: `${tag}:bus` });
  let voices = [], played = 0, steals = 0, maxActive = 0;   // { v, midi, when, end, lvl, g }

  /** a voice's shell: sources into k.amp (the envelope) → its level → its pan → the bus */
  function shell(pan) {
    const nodes = [], src = [], N = n => (nodes.push(n), n);
    const amp = N(ac.createGain()), lvl = N(ac.createGain()), p = N(ac.createStereoPanner());
    amp.gain.value = 0; p.pan.value = pan;
    amp.connect(lvl); lvl.connect(p); p.connect(bus);
    return {
      amp, lvl, nodes, src, N,
      G: (v = 0) => { const g = N(ac.createGain()); g.gain.value = v; return g; },
      O: (f, wave = null) => { const o = N(ac.createOscillator()); o.frequency.value = f; if (wave) o.setPeriodicWave(wave); src.push(o); return o; },
    };
  }
  /** the envelope of a struck note: up in `atk`, settling to settle[0] of the peak (τ settle[1]), ringing off from
   *  there (τ `ring`, shorter up the keyboard); returns when it has ended */
  function strike(k, when, f, V) {
    const g = k.amp.gain, tA = when + V.atk, tB = tA + 2.5 * V.settle[1], tau = V.ring * clamp(Math.pow(523 / f, 0.35), 0.6, 1.3);
    g.setValueAtTime(0, when); g.linearRampToValueAtTime(1, tA);
    g.setTargetAtTime(V.settle[0], tA, V.settle[1]);
    g.setTargetAtTime(0, tB, tau);
    return Math.min(when + 3.5, tB + 5.5 * tau);
  }
  function tone(k, when, midi, V) {
    const f = mtof(midi), wave = V.wave ? V.wave(ac) : null;
    let into = k.amp;
    if (V.lp) {                                         // [opens at × f, closes to × f, τ] or a fixed cutoff
      const lp = k.N(ac.createBiquadFilter()), [open, shut, tau, fixed] = V.lp;
      lp.type = 'lowpass'; lp.Q.value = 0.6;
      lp.frequency.value = fixed || Math.min(open * f, 9000);
      if (!fixed) { lp.frequency.setValueAtTime(Math.min(open * f, 9000), when); lp.frequency.setTargetAtTime(Math.min(shut * f, 6000), when + V.atk, tau); }
      lp.connect(k.amp); into = lp;
    }
    const end = strike(k, when, f, V), life = end - when;
    if (V.fm) {
      const [ratio, index, floor, tau] = V.fm, I = index * clamp(Math.sqrt(440 / f), 0.5, 1.2);
      const car = k.O(f), mod = k.O(ratio * f), mg = k.G(0);
      mg.gain.setValueAtTime(I * ratio * f, when); mg.gain.setTargetAtTime(floor * I * ratio * f, when, tau);
      mod.connect(mg); mg.connect(car.frequency); car.connect(into);
      if (V.tine && V.tine[0] * f < 0.4 * ac.sampleRate) {
        const [tr, ti, tt] = V.tine, t = k.O(tr * f), tg = k.G(0);
        tg.gain.setValueAtTime(ti * Math.min(1, Math.pow(440 / f, 0.7)) * tr * f, when); tg.gain.setTargetAtTime(0, when, tt);
        t.connect(tg); tg.connect(car.frequency);
      }
    } else for (const [ratio, level, share] of V.partials) {
      if (ratio * f > 0.45 * ac.sampleRate) continue;
      const o = k.O(ratio * f, wave), g = k.G(level);
      if (share < 1) g.gain.setTargetAtTime(0, when + V.atk, share * life / 5);   // the upper modes die first
      o.connect(g); g.connect(into);
    }
    return end;
  }
  function string(k, when, midi, V) {
    const { buf, rate } = V.string(ac, midi), s = k.N(ac.createBufferSource()), lp = k.N(ac.createBiquadFilter());
    s.buffer = buf; s.playbackRate.value = rate;
    lp.type = 'lowpass'; lp.frequency.value = 3200; lp.Q.value = 0.5;   // (the styles' own buses round the pluck off too)
    s.connect(lp); lp.connect(k.amp); k.src.push(s);
    // the string under a soft finger (5 ms in: the pick's first spike is 10 dB over the note), then damped: it rings
    // off from .3 s (τ .35 s), inside its buffer
    const g = k.amp.gain;
    g.setValueAtTime(0, when); g.linearRampToValueAtTime(1, when + 0.005); g.setTargetAtTime(0, when + 0.3, 0.35);
    return when + Math.min(buf.duration / rate, 2);
  }

  return {
    note(when, midi, style, i = 0) {
      const now = ac.currentTime;
      voices = voices.filter(x => x.end > now);
      const together = voices.filter(x => Math.abs(x.when - when) < 1e-4);
      if (together.some(x => x.midi === midi)) return false;
      for (const x of voices) if (x.midi === midi && x.when < when && x.end > when) { registry.stop(x.v, when, 0.012); x.end = Math.min(x.end, when + 0.1); }
      const live = voices.filter(x => x.end > when);
      if (live.length >= cap) { const old = live[0]; registry.stop(old.v, when, 0.012); old.end = when; steals++; }
      const V = JAM_VOICES[jamVoice(style)], k = shell(clamp((i - (JAM_KEYS - 1) / 2) / (JAM_KEYS - 1) * 0.6, -0.3, 0.3));
      const end = V.string ? string(k, when, midi, V) : tone(k, when, midi, V);
      k.amp.gain.setTargetAtTime(0, end - 0.05, 0.012);   // whatever is left, gone before the sources stop
      for (const s of k.src) { s.start(when); s.stop(end); }
      const x = { v: registry.add({ src: k.src, all: k.nodes, start: when, end, tag, out: k.amp }), midi, when, end, lvl: k.lvl, g: V.gain };
      voices.push(x);
      together.push(x);
      for (const y of together) y.lvl.gain.value = y.g / Math.sqrt(together.length);   // not sounding yet: set, not ramped
      played++;
      maxActive = Math.max(maxActive, Math.min(cap, live.length + 1));
      return true;
    },
    stats: () => ({ played, steals, maxActive, cap, active: voices.filter(x => x.end > ac.currentTime).length, voices: registry.count(tag) }),
    dispose() {
      registry.stopTag(tag, ac.currentTime, 0.02);
      registry.free(busVoice);
    },
  };
}
